import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import lediumApi from '@client/utils/lediumApi';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, InputBaseComponentProps, TextField, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

const Root = styled('div')(
    () => `
  ul {
    overflow: hidden;
  }
`
);

export interface ZoneItem {
    id: string;
    name: string;
    description?: string;
    parent?: string;
    locked?: string;
    linked?: string;
}

interface ZoneTreeNode extends ZoneItem {
    children: ZoneTreeNode[];
}

interface OutlinedDivProps {
    children: React.ReactNode;
    label?: React.ReactNode;
}

const InputComponent = (props: React.ComponentPropsWithoutRef<'div'>) => <div {...props} />;

const OutlinedDiv = ({ children, label }: OutlinedDivProps) => (
    <TextField
        variant="outlined"
        label={label}
        multiline
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{
            inputComponent: InputComponent as React.ComponentType<InputBaseComponentProps>
        }}
        inputProps={{ children: children }}
    />
);

const openAllOnInitThreshold = 20;

interface SelectZoneTreeProps {
    label?: React.ReactNode;
    itemList?: ZoneItem[];
    defaultSelected?: string;
    onSelect?: (item: ZoneItem | undefined) => void;
    onEdit?: (item: ZoneItem) => void;
    denySelectBelow?: string;
    disabledMessage?: React.ReactNode;
    disabled?: boolean;
}

const SelectZoneTree = React.memo(
    ({ label, itemList, defaultSelected, onSelect, onEdit, denySelectBelow, disabledMessage = null, disabled = false }: SelectZoneTreeProps) => {
        const {
            pageState: { formData }
        } = usePageStore();
        const [zones, setZones] = React.useState<ZoneItem[]>([]);
        const [zoneData, setZoneData] = React.useState<Partial<ZoneTreeNode>>({});
        const [expanded, setExpanded] = React.useState<string[]>([]);
        const [selected, setSelected] = React.useState<string>('');

        const buildTree = React.useCallback((groupItems: ZoneItem[]) => {
            const root: ZoneItem = {
                id: 'root',
                name: 'Root category',
                linked: 'ROOT_CATEGORY',
                locked: 'ROOT_CATEGORY'
            };
            if (!groupItems.find((g) => g.id === 'root')) {
                groupItems.unshift(root);
            }

            const hashTable: Record<string, ZoneTreeNode> = Object.create(null);
            groupItems.forEach((aData) => {
                hashTable[aData.id] = { ...aData, children: [] };
            });
            const dataTree: ZoneTreeNode[] = [];
            groupItems.forEach((aData) => {
                if (aData.parent && hashTable[aData.parent]) {
                    hashTable[aData.parent].children.push(hashTable[aData.id]);
                } else {
                    dataTree.push(hashTable[aData.id]);
                }
            });
            setZoneData(dataTree.shift() ?? {});
        }, []);

        const searchTree = React.useCallback(
            (searchId: string, struct: Partial<ZoneTreeNode> = zoneData): string[] | null => {
                if (struct.id === searchId) {
                    return [];
                } else if (struct.children) {
                    for (const child of struct.children) {
                        const path = searchTree(searchId, child);
                        if (path !== null) {
                            path.unshift(child.id);
                            return path;
                        }
                    }
                }
                return null;
            },
            [zoneData]
        );

        function handleToggle(event: React.SyntheticEvent, nodeIds: string[]) {
            setExpanded(nodeIds);
        }

        function handleSelect(event: React.SyntheticEvent, nodeIds: string | string[] | null) {
            if (!nodeIds) {
                return;
            }
            const nodeId = Array.isArray(nodeIds) ? nodeIds[0] : nodeIds;
            if (denySelectBelow) {
                const pathToSelected = searchTree(nodeId);
                if (pathToSelected && pathToSelected.includes(denySelectBelow)) {
                    return null;
                }
            }

            setSelected(nodeId);
            if (typeof onSelect === 'function') {
                const selectedItem = zones.find((item) => item.id === nodeId);
                onSelect(selectedItem);
            }
        }

        const setZoneItems = React.useCallback(
            (groupsData: ZoneItem[]) => {
                setZones(groupsData);
                buildTree(groupsData);
                if (onSelect && defaultSelected) {
                    const selectedItem = groupsData.find((item) => item.id === defaultSelected);
                    onSelect(selectedItem);
                }
            },
            [buildTree, defaultSelected, onSelect]
        );

        React.useEffect(() => {
            if (itemList) {
                setZoneItems(itemList);
            } else {
                lediumApi.get<ZoneItem[]>('zones').then((groupsData) => groupsData && setZoneItems(groupsData));
            }
        }, [itemList, setZoneItems]);

        React.useEffect(() => {
            if (zoneData.id && defaultSelected) {
                if (zones && zones.length > openAllOnInitThreshold) {
                    const tree = searchTree(defaultSelected) || [];
                    setExpanded(['root', ...tree]);
                } else {
                    setExpanded(zones.map((g) => g.id));
                }
                setSelected(defaultSelected);
            }
        }, [searchTree, defaultSelected, zoneData, zones]);

        const renderTree = (nodes: ZoneTreeNode): React.ReactElement => (
            <TreeItem
                key={nodes.id}
                itemId={nodes.id}
                label={
                    <div style={{ padding: '4px 0' }} onDoubleClick={() => onEdit && nodes.id !== 'root' && onEdit(nodes)}>
                        <Typography display="inline" variant="body1" sx={{ fontSize: '16px', fontWeight: 500 }}>
                            {nodes.name}
                        </Typography>
                        {nodes.description && (
                            <Typography display="inline" variant="body2" sx={{ fontSize: '13px', marginLeft: '5px' }}>
                                ({nodes.description})
                            </Typography>
                        )}
                    </div>
                }
            >
                {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
            </TreeItem>
        );

        if (!zoneData.id) {
            return null;
        }
        return (
            <Root>
                <OutlinedDiv label={label}>
                    {disabledMessage && (
                        <Alert sx={{ marginBottom: '8px' }} severity="warning">
                            {disabledMessage}
                        </Alert>
                    )}
                    <SimpleTreeView
                        slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
                        defaultExpandedItems={['root']}
                        expandedItems={expanded}
                        selectedItems={selected}
                        disableSelection={disabled || !!formData.readOnly}
                        disabledItemsFocusable
                        onExpandedItemsChange={handleToggle}
                        onSelectedItemsChange={handleSelect}
                    >
                        {renderTree(zoneData as ZoneTreeNode)}
                    </SimpleTreeView>
                </OutlinedDiv>
            </Root>
        );
    }
);

SelectZoneTree.displayName = 'SelectZoneTree';
export { SelectZoneTree };
